import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import AutocompleteAdresse from './AutocompleteAdresse';

interface QuickEstimateProps {
  onStartEstimation: (address: string) => void;
}

export default function QuickEstimate({ onStartEstimation }: QuickEstimateProps) {
  const [address, setAddress] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) {
      setShowValidation(true);
      setTimeout(() => setShowValidation(false), 2500);
      return;
    }
    onStartEstimation(address);
    setAddress('');
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-[#4A90E2]/30 via-[#7B8794]/20 to-[#DDB65D]/30 blur-[100px] pointer-events-none"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#7B8794]/20 to-[#4A90E2]/30 pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(74,144,226,0.2),transparent_70%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(221,182,93,0.2),transparent_70%)] pointer-events-none"></div>

      <div className="relative mx-auto">
        <div 
          className="bg-white/80 backdrop-blur-xl p-6 sm:p-8 lg:p-12 shadow-[0_8px_32px_rgba(74,144,226,0.15)]
            transform hover:scale-[1.02] transition-all duration-300 
            before:content-[''] before:absolute before:inset-0
            before:bg-gradient-to-b before:from-white/30 before:to-[#4A90E2]/10 before:pointer-events-none
            [transform-style:preserve-3d] hover:[transform:perspective(1000px)_rotateX(2deg)]
            after:content-[''] after:absolute after:inset-0
            after:bg-gradient-to-r after:from-[#4A90E2]/10 after:via-[#7B8794]/10 after:to-[#DDB65D]/10 
            after:pointer-events-none relative overflow-hidden"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
              <div className={`${
                showValidation ? 'animate-[pulse_0.5s_ease-in-out_5]' : ''
              } absolute -inset-0.5 rounded-xl bg-gradient-to-r from-[#4A90E2] via-[#7B8794] to-[#DDB65D] transition-opacity ${
                showValidation ? 'opacity-100' : 'opacity-0'
              }`} />
              <AutocompleteAdresse
                onSelect={setAddress}
                value={address}
                placeholder="Entrez l'adresse de votre bien"
              />
            </div>
            
            <button
              type="submit"
              className="w-full flex items-center justify-center px-4 sm:px-8 py-3 sm:py-5 text-base sm:text-xl font-medium 
                rounded-xl text-white bg-gradient-to-r from-[#4A90E2] via-[#7B8794] to-[#DDB65D]
                hover:from-[#3A80D2] hover:via-[#6B7784] hover:to-[#CDA64D]
                focus:outline-none focus:ring-2 focus:ring-[#4A90E2] focus:ring-offset-2 
                transform transition-all duration-300 hover:scale-105 hover:-translate-y-1
                shadow-lg hover:shadow-xl hover:shadow-[#4A90E2]/20"
            >
              Démarrer mon estimation
              <ArrowRight className="ml-2 h-5 w-5 sm:h-7 sm:w-7" strokeWidth={2.5} />
            </button>
          </form>

          <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-12 text-center">
            <div className="transform transition-all duration-300 hover:-translate-y-1 hover:scale-105">
              <div className="text-2xl sm:text-4xl font-bold text-gray-900">
                100%
              </div>
              <div className="mt-2 sm:mt-3 text-base sm:text-lg text-gray-700">Gratuit et sans engagement</div>
            </div>
            <div className="transform transition-all duration-300 hover:-translate-y-1 hover:scale-105">
              <div className="text-2xl sm:text-4xl font-bold text-gray-900">
                2 min
              </div>
              <div className="mt-2 sm:mt-3 text-base sm:text-lg text-gray-700">Estimation rapide</div>
            </div>
            <div className="transform transition-all duration-300 hover:-translate-y-1 hover:scale-105">
              <div className="text-2xl sm:text-4xl font-bold text-gray-900">
                95%
              </div>
              <div className="mt-2 sm:mt-3 text-base sm:text-lg text-gray-700">De précision</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}